import { fireOnce, trackStructuredEvent } from 'src/analytics';
import { getCategory } from 'src/interviews/analytics';

import type { IDiagnosis, MiniInterviewType, TreatmentSupplier } from 'src/packages/polaris/types';
import type { RelatedReadingType } from './components/RelatedReading/RelatedReading';

export const trackEmailCollectionViewed = ({
  descriptionText,
  interviewToken,
  interviewType,
  submitText,
}: {
  interviewToken: string;
  interviewType: MiniInterviewType | string;
  descriptionText?: string;
  submitText?: string;
}) => {
  const event = {
    category: getCategory(interviewType),
    action: 'rich-ending-email-collection-viewed',
    label: interviewToken,
    property: `${descriptionText || ''} | ${submitText || ''}`,
  };

  fireOnce(() => trackStructuredEvent(event), Object.values(event).join(':'));
};

export const trackEmailCollectionSubmitted = ({
  descriptionText,
  interviewToken,
  interviewType,
  submitText,
}: {
  descriptionText?: string;
  interviewToken: string;
  interviewType: MiniInterviewType | string;
  submitText?: string;
}) => {
  const event = {
    category: getCategory(interviewType),
    action: 'rich-ending-email-collection-submitted',
    label: interviewToken,
    property: `${descriptionText || ''} | ${submitText || ''}`,
  };

  fireOnce(() => trackStructuredEvent(event), Object.values(event).join(':'));
};

/** To enable the study of other matches we are rendering, whether or not a user sees them */
export const trackOtherMatches = (
  interviewToken: string,
  interviewType: MiniInterviewType,
  otherMatches: IDiagnosis[],
) => {
  const event = {
    category: getCategory(interviewType),
    action: 'conclusion-other-matches',
    label: interviewToken,
    property: otherMatches?.map((match) => match.name)?.join(',') || '',
  };
  const key = `${event.category}:${event.action}:${event.label}:${event.property}`;
  fireOnce(() => trackStructuredEvent(event), `${key}`);
};

/** To enable the study of what treatments we are recommending, whether or not a user sees them */
export const trackTreatmentsForYou = (
  interviewToken: string,
  interviewType: MiniInterviewType,
  treatments: TreatmentSupplier[],
) => {
  const event = {
    category: getCategory(interviewType),
    action: 'conclusion-treatments-offered',
    label: interviewToken,
    property: JSON.stringify(treatments.map((treatment) => treatment.url)).substring(0, 949),
  };
  const key = `${event.category}:${event.action}:${event.label}:${event.property}`;
  fireOnce(() => trackStructuredEvent(event), `${key}`);
};

/** To enable the study of what related readings we are recommending, whether or not a user sees them */
export const trackRelatedReadings = (
  interviewToken: string,
  interviewType: MiniInterviewType,
  relatedReadings: RelatedReadingType[],
) => {
  const event = {
    category: getCategory(interviewType),
    action: 'conclusion-related-readings-offered',
    label: interviewToken,
    property: JSON.stringify(relatedReadings.map((reading) => reading.url)).substring(0, 949),
  };
  const key = `${event.category}:${event.action}:${event.label}:${event.property}`;
  fireOnce(() => trackStructuredEvent(event), `${key}`);
};

export const trackGetVirtualCareModuleViewed = ({
  interviewToken,
  interviewType,
  location,
  url,
}: {
  interviewToken: string;
  interviewType: MiniInterviewType;
  location: 'results' | 'treatment-plan';
  url: string;
}) => {
  const event = {
    category: `${getCategory(interviewType)}-${location}`,
    action: 'get-virtual-care-cta-viewed',
    label: interviewToken,
    property: url,
  };
  const key = `${event.category}:${event.action}:${event.label}:${event.property}`;
  fireOnce(() => trackStructuredEvent(event), `${key}`);
};

export const trackGetVirtualCareModuleClicked = ({
  interviewToken,
  interviewType,
  location,
  url,
}: {
  interviewToken: string;
  interviewType: MiniInterviewType;
  location: 'results' | 'treatment-plan';
  url: string;
}) => {
  const event = {
    category: `${getCategory(interviewType)}-${location}`,
    action: 'get-virtual-care-cta-clicked',
    label: interviewToken,
    property: url,
  };
  const key = `${event.category}:${event.action}:${event.label}:${event.property}`;
  fireOnce(() => trackStructuredEvent(event), `${key}`);
};

export const trackViewMoreDDxClicked = ({
  interviewToken,
  interviewType,
}: {
  interviewToken: string;
  interviewType: MiniInterviewType;
}) => {
  const event = {
    category: getCategory(interviewType),
    action: 'view-more-ddx-button-clicked',
    label: interviewToken,
  };
  trackStructuredEvent(event);
};
